import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { getClaimResult, type ClaimResultSummary } from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

// FUTURE: a "Contact support" fallback (shown on the mismatch / error cases below, so a claimer whose
// GitHub identity doesn't match the repo can reach us) will go here — a mailto to a real support
// inbox. Intentionally omitted until that inbox exists.

type Tone = "success" | "warning";

// Landing page the GitHub consent callback redirects to.
// Single: ?status=consented|removed|mismatch|github_auth_failed|error & slug & repo
// Bulk:   ?result=<id>  → fetch the per-skill success/failure summary
export default function ClaimResult() {
  useDocumentTitle("Claim · SkillPatch");
  const [params] = useSearchParams();
  const resultId = params.get("result");

  const [summary, setSummary] = useState<ClaimResultSummary | null>(null);
  const [loading, setLoading] = useState(!!resultId);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!resultId) return;
    getClaimResult(resultId)
      .then(setSummary)
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [resultId]);

  if (resultId) {
    if (loading) return <Centered>Loading your results…</Centered>;
    if (loadError || !summary) return <Centered>We couldn’t load your claim results. Please try again.</Centered>;
    return <BulkResult summary={summary} />;
  }

  return <SingleResult />;
}

function Centered({ children }: { children: React.ReactNode }) {
  return <main className="mx-auto flex min-h-[60vh] max-w-xl items-center justify-center px-6 text-text/50">{children}</main>;
}

function IconTile({ tone }: { tone: Tone }) {
  const ring =
    tone === "success"
      ? "border-green-500/30 bg-green-500/10 text-green-400"
      : "border-amber-500/30 bg-amber-500/10 text-amber-400";
  return (
    <div className={`flex h-20 w-20 items-center justify-center rounded-2xl border ${ring}`}>
      {tone === "success" ? (
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
      ) : (
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01" /><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg>
      )}
    </div>
  );
}

const primaryBtn =
  "rounded-[12px] bg-primary px-6 py-2.5 font-display font-semibold text-white shadow-btn transition-all duration-200 hover:bg-primary-hover hover:scale-[1.04] hover:shadow-[0_0_28px_rgba(225,100,46,0.5)] active:scale-[0.98]";

// ── Bulk: per-skill summary ────────────────────────────────────────────────
function BulkResult({ summary }: { summary: ClaimResultSummary }) {
  const isTakedown = summary.intent === "takedown";
  const okVerb = isTakedown ? "Removed" : "Claimed";
  const okCount = summary.succeeded.length;
  const tone: Tone = okCount > 0 ? "success" : "warning";

  const title =
    okCount === 0
      ? "We couldn’t verify any skills"
      : `${okCount} skill${okCount === 1 ? "" : "s"} ${isTakedown ? "removed" : "claimed"}`;

  return (
    <main className="mx-auto flex min-h-[70vh] max-w-2xl flex-col items-center px-6 py-16 text-center">
      <IconTile tone={tone} />
      <h1 className="mt-7 font-display text-3xl font-bold tracking-tight">{title}</h1>
      <p className="mt-3 text-[15px] text-text/60">
        Verified as <span className="font-semibold text-text/80">{summary.login}</span> for{" "}
        <span className="font-semibold text-text/80">{summary.owner}</span>.
      </p>

      <div className="mt-8 w-full space-y-5 text-left">
        {summary.succeeded.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-green-400">{okVerb} · {summary.succeeded.length}</h2>
            <ul className="mt-2 divide-y divide-white/[0.06] overflow-hidden rounded-xl border border-white/10">
              {summary.succeeded.map((s) => (
                <li key={s.slug} className="flex items-center gap-3 px-4 py-3">
                  <svg className="shrink-0 text-green-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
                  <span className="min-w-0 flex-1 truncate text-sm text-text/80">{s.name}</span>
                  {!isTakedown && (
                    <Link to={`/skill/${s.slug}`} className="shrink-0 text-xs text-text/40 transition hover:text-primary">View →</Link>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {summary.failed.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-amber-400">Couldn’t verify · {summary.failed.length}</h2>
            <ul className="mt-2 divide-y divide-white/[0.06] overflow-hidden rounded-xl border border-white/10">
              {summary.failed.map((s) => (
                <li key={s.slug} className="flex items-center gap-3 px-4 py-3">
                  <svg className="shrink-0 text-amber-400/80" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-text/80">{s.name}</p>
                    {s.reason && <p className="truncate text-xs text-text/40">{s.reason}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <Link to={`/skills?tab=official&publisher=${encodeURIComponent(summary.owner)}`} className={primaryBtn}>
          Back to publisher
        </Link>
        {/* FUTURE: when some skills failed to verify, a "Contact support" link (mailto to the support
            inbox) belongs here so the claimer can reach us. Omitted until that inbox exists. */}
      </div>
    </main>
  );
}

// ── Single-skill result ────────────────────────────────────────────────────
function SingleResult() {
  const [params] = useSearchParams();
  const status = params.get("status") || "";
  const slug = params.get("slug") || "";
  const repo = params.get("repo") || "";

  let tone: Tone = "success";
  let title = "";
  let body: React.ReactNode = null;
  let backToSkill = true;

  switch (status) {
    case "consented":
      tone = "success";
      title = "You’re verified — welcome aboard";
      body = (
        <>
          We’ve confirmed you as the author. This skill now carries a{" "}
          <span className="font-semibold text-text/80">“claimed”</span> badge — a signal to every
          developer who finds it that this comes straight from you.
        </>
      );
      break;
    case "removed":
      tone = "success";
      title = "Done — your skill has been removed";
      body = (
        <>
          We’ve taken <span className="font-semibold text-text/80">{slug || "this skill"}</span> down
          and it won’t be re-imported. Thanks for letting us know, and apologies for any inconvenience.
        </>
      );
      backToSkill = false;
      break;
    case "mismatch":
      tone = "warning";
      title = "We couldn’t confirm you’re the author";
      body = (
        <>
          The GitHub account you signed in with doesn’t have access to{" "}
          <span className="font-semibold text-text/80">{repo || "the source repository"}</span>, so we
          couldn’t verify ownership. If this skill is yours, sign in with the GitHub account that owns
          that repository and try again.
        </>
      );
      break;
    case "github_auth_failed":
      tone = "warning";
      title = "GitHub sign-in didn’t finish";
      body = <>We couldn’t complete verification with GitHub. Head back to the skill and try again — it only takes a moment.</>;
      break;
    case "error":
      tone = "warning";
      title = "Something went wrong";
      body = <>We couldn’t finish processing your request. Please head back to the skill and try again in a moment.</>;
      break;
    default:
      tone = "warning";
      title = "Nothing to show here";
      body = <>This page appears after you claim a skill.</>;
      backToSkill = false;
  }

  return (
    <main className="mx-auto flex min-h-[70vh] max-w-xl flex-col items-center justify-center px-6 py-20 text-center">
      <IconTile tone={tone} />
      <h1 className="mt-7 font-display text-3xl font-bold tracking-tight">{title}</h1>
      <p className="mt-3 max-w-md text-[15px] leading-relaxed text-text/60">{body}</p>

      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        {/* FUTURE: on the mismatch / error cases, a "Contact support" button (mailto to the support
            inbox) belongs here so the claimer can reach us. Omitted until that inbox exists. */}
        {backToSkill && slug ? (
          <Link to={`/skill/${slug}`} className={primaryBtn}>Back to the skill</Link>
        ) : (
          <Link to="/skills" className={primaryBtn}>Browse skills</Link>
        )}
      </div>
    </main>
  );
}
